import React, { useState } from 'react';
import { TableCell, TextField, IconButton, Box, Tooltip, Typography } from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon, AutoMode as AutoModeIcon } from '@mui/icons-material';

interface EditableTableCellProps {
  value?: number;
  onSave: (value: number | undefined) => void;
  formatValue?: (value: number) => string;
  placeholder?: string;
  align?: 'left' | 'center' | 'right';
  helperText?: string;
  isAutoCalculated?: boolean;
}

const EditableTableCell: React.FC<EditableTableCellProps> = ({
  value,
  onSave,
  formatValue = (v) => `$${v.toFixed(2)}`,
  placeholder = 'Click to edit',
  align = 'right',
  helperText,
  isAutoCalculated = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value?.toString() || '');

  const handleSave = () => {
    const numValue = editValue ? parseFloat(editValue) : undefined;
    if (editValue && !isNaN(numValue!)) {
      onSave(numValue);
      setIsEditing(false);
    } else if (!editValue) {
      onSave(undefined);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value?.toString() || '');
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <TableCell align={align}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            size="small"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyPress}
            autoFocus
            type="number"
            sx={{ width: '100px' }}
            inputProps={{ step: 0.01 }}
          />
          <IconButton size="small" onClick={handleSave} color="success">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleCancel} color="error">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
    );
  }

  return (
    <TableCell 
      align={align} 
      sx={{ 
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
        minWidth: '120px'
      }}
      onClick={() => {
        setEditValue(value?.toString() || '');
        setIsEditing(true);
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', gap: 1 }}>
        <Box>
          {value ? formatValue(value) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>{placeholder}</span>
          )}
          {helperText && (
            <Typography variant="caption" display="block" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
              {helperText}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isAutoCalculated && (
            <Tooltip title="Auto-calculated from market data">
              <AutoModeIcon fontSize="small" sx={{ opacity: 0.6, color: 'primary.main' }} />
            </Tooltip>
          )}
          <EditIcon fontSize="small" sx={{ opacity: 0.6 }} />
        </Box>
      </Box>
    </TableCell>
  );
};

export default EditableTableCell;